// ISDP CUSTOM: DevHub-branded route-level error boundary.
//
// A render crash anywhere in the route tree otherwise blanks the whole app to a
// white screen. This catches it, reports to Sentry, and shows an ODX-styled
// recovery card with "Try again" (re-render) and "Go home" (hard reset) — so a
// single broken page degrades gracefully instead of taking down the DevHub.
import { Component, type ErrorInfo, type ReactNode } from 'react';
import * as Sentry from '@sentry/browser';
import './DevHubErrorBoundary.css';

interface Props {
  children: ReactNode;
}
interface State {
  error: Error | null;
}

export class DevHubErrorBoundary extends Component<Props, State> {
  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  state: State = { error: null };

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Report with the component stack for triage; never throw from here.
    try {
      Sentry.captureException(error, {
        extra: { componentStack: info.componentStack },
      });
    } catch {
      /* reporting must never mask the original error */
    }
  }

  private handleRetry = () => this.setState({ error: null });

  private handleHome = () => {
    window.location.assign('/');
  };

  render() {
    if (!this.state.error) {
      return this.props.children;
    }
    return (
      <div className="devhub-errorboundary" role="alert">
        <div className="devhub-errorboundary__card">
          <odx-icon name="core::warning" size="lg" />
          <h1 className="devhub-errorboundary__title">Something went wrong</h1>
          <p className="devhub-errorboundary__msg">
            This page hit an unexpected error. The rest of the DevHub is fine — the
            issue has been reported to the platform team.
          </p>
          <div className="devhub-errorboundary__actions">
            <odx-button
              type="button"
              variant="primary"
              className="devhub-errorboundary__btn devhub-errorboundary__btn--primary"
              onClick={this.handleRetry}
            >
              Try again
            </odx-button>
            <odx-button
              type="button"
              variant="secondary"
              className="devhub-errorboundary__btn"
              onClick={this.handleHome}
            >
              Go to Home
            </odx-button>
          </div>
        </div>
      </div>
    );
  }
}
